import React, { useState } from "react";
import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { getUserSubscriptionsWithExpired, useQuery } from "wasp/client/operations";
import { createPortal } from "react-dom";
import { useTranslation } from "../../i18n/hooks";

const UsageTable = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [showTooltip, setShowTooltip] = useState(false);

  const { data: subscriptions, isLoading: subsLoading } =
    useQuery(getUserSubscriptionsWithExpired);

  const handleMouseEnter = (e: React.MouseEvent) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltipPosition({
      x: rect.right,
      y: rect.top - 10,
    });
    setShowTooltip(true);
  };

  const formatNumber = (num: number): string => {
    const formats = [
      { value: 1000000000, symbol: "B" },
      { value: 1000000, symbol: "M" },
      { value: 1000, symbol: "K" },
    ];

    for (const format of formats) {
      if (num >= format.value) {
        const formatted = (num / format.value).toFixed(1);
        // Ondalık kısım 0 ise onu kaldır
        return formatted.endsWith(".0")
          ? formatted.slice(0, -2) + format.symbol
          : formatted + format.symbol;
      }
    }

    return num.toString();
  };

  const Tooltip = () => (
    <div>
      {!subsLoading &&
        subscriptions &&
        subscriptions.filter((sub) => sub.status === "ACTIVE").length > 0 && (
          <div
            className="fixed bg-slate-900 text-white py-2 px-3 rounded-md shadow-lg z-[9999]"
            style={{
              left: tooltipPosition.x + 10,
              top: tooltipPosition.y,
            }}
          >
            <p className="font-medium mb-1 text-xs">{t("usageTable.plan_details")}</p>
            <p className="text-xs text-gray-200">
              {t("usageTable.assigned_credits")}{" "}
              {
                subscriptions.filter((sub) => sub.status === "ACTIVE")[0].plan
                  .creditsPerPeriod
              }{" "}
              AI Credits
            </p>
            <p className="text-xs text-gray-200">
              {t("usageTable.used_credits")}{" "}
              {subscriptions.filter((sub) => sub.status === "ACTIVE")[0].plan
                .creditsPerPeriod -
                subscriptions.filter((sub) => sub.status === "ACTIVE")[0]
                  .creditBalances[0].remainingCredits}{" "}
              AI Credits
            </p>
            <p className="text-xs text-gray-200">
              {t("usageTable.remaining_credits")}{" "}
              {
                subscriptions.filter((sub) => sub.status === "ACTIVE")[0]
                  .creditBalances[0].remainingCredits
              }{" "}
              AI Credits
            </p>
            <div className="absolute left-[-8px] top-[50%] -translate-y-1/2 border-[6px] border-transparent border-r-slate-900" />
          </div>
        )}
    </div>
  );

  return (
    <div className="mx-3 mt-6">
      <div className="rounded-xl border border-gray-100 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-900">
        {!subsLoading &&
          subscriptions &&
          subscriptions.length > 0 &&
          subscriptions.filter((sub) => sub.status === "ACTIVE").length > 0 && (
            <div className="p-4">
              {/* Title row with info icon */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {
                    subscriptions.filter((sub) => sub.status === "ACTIVE")[0]
                      .plan.name
                  }
                </h3>
                <div
                  onMouseEnter={handleMouseEnter}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <Info className="h-3.5 w-3.5 text-gray-400 hover:text-gray-600 cursor-help transition-colors" />
                </div>
              </div>

              {/* Credits usage row */}
              <div className="space-y-3">
                <div className="grid grid-cols-[auto,1fr] gap-1 items-center">
                  <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {t("usageTable.ai_credits")}
                  </span>
                  <div className="text-right text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {formatNumber(
                      subscriptions.filter((sub) => sub.status === "ACTIVE")[0]
                        .creditBalances[0].remainingCredits
                    )}{" "}
                    /{" "}
                    {formatNumber(
                      subscriptions.filter((sub) => sub.status === "ACTIVE")[0]
                        .plan.creditsPerPeriod
                    )}
                  </div>
                </div>

                {/* Progress bar with gradient */}
                <div className="h-1.5 w-full rounded-full bg-gray-100 dark:bg-gray-800">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-blue-500 to-blue-400"
                    style={{
                      width: `${Math.min(
                        (subscriptions.filter(
                          (sub) => sub.status === "ACTIVE"
                        )[0].creditBalances[0].remainingCredits /
                          subscriptions.filter(
                            (sub) => sub.status === "ACTIVE"
                          )[0].plan.creditsPerPeriod) *
                          100,
                        100
                      )}%`,
                      transition: "width 0.3s ease-in-out",
                    }}
                  />
                </div>

                {/* Upgrade button with gradient hover */}
                <button
                  onClick={() => navigate("/admin/upgrade-plan")}
                  className="w-full mt-4 py-2 px-4 bg-blue-500 hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 text-white text-xs font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                >
                  {t("usageTable.upgrade_now")}
                </button>
              </div>
            </div>
          )}
        {subscriptions &&
          subscriptions.length > 0 &&
          subscriptions.filter((sub) => sub.status === "ACTIVE").length === 0 &&
          subscriptions.filter((sub) => sub.status === "EXPIRED").length >
            0 && (
            <div className="p-4">
              {/* Title row with info icon */}
              <div className="flex items-center justify-between mb-5">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {t("usageTable.subscription_expired")}
                </h3>
              </div>

              {/* Credits usage row */}
              <div className="space-y-3">
                {/* Upgrade button with gradient hover */}
                <button
                  onClick={() => navigate("/admin/upgrade-plan")}
                  className="w-full mt-4 py-2 px-4 bg-blue-500 hover:bg-gradient-to-r hover:from-blue-500 hover:to-blue-600 text-white text-xs font-semibold rounded-lg transition-all duration-200 shadow-sm hover:shadow"
                >
                  {t("usageTable.upgrade_now")}
                </button>
              </div>
            </div>
          )}
      </div>
      {showTooltip && createPortal(<Tooltip />, document.body)}
    </div>
  );
};

export default UsageTable;
